//Proofed
import { useState } from 'react'
import { Box, Container, Grid, Typography, Stack, Chip, Card, CardContent, Accordion, AccordionSummary, AccordionDetails } from '@mui/material'
import { BookDemoModal } from '../../components/BookDemoModal'
import { FancyGoldButton } from '../../components/FancyGoldButton'
import { FancyLightButton } from '../../components/FancyLightButton'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import InsightsIcon from '@mui/icons-material/Insights'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PlayCircleIcon from '@mui/icons-material/PlayCircle'
import FormatQuoteIcon from '@mui/icons-material/FormatQuote'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import StarIcon from '@mui/icons-material/Star'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { COLOR_QUATERNARY, FORESIGHT_HERO_START, FORESIGHT_HERO_MID, FORESIGHT_HERO_END, CHECKCIRCLE_SIZE, SECTION_DARK, SECTION_LIGHT, BG_LIGHT, DARK_800 } from '../../theme'
import { TrustedPartnerSection } from '../../components/TrustedPartnerSection'
import { HowItWorksSection } from '../../components/HowItWorksSection'
import PageMeta from '../../components/PageMeta'
import { VideoModal } from '../../components/VideoModal'
import { StatsSection } from '../../components/StatsSection'
import { CtaSection } from '../../components/CtaSection'
import { ANALYTICS_VIDEO_ID } from '../../common/HomeContent'
import { REVIEWS_URL } from '../../common/common'
import { analyticsFaqs, analyticsFeatures, analyticsHighlights, analyticsHIWSubtitle, analyticsHIWTitle, analyticsStats, analyticsSteps, analyticsWhoUses } from '../../common/ProductAnalyticsContent'
import { WaveDivider } from '../../components/WaveDivider'
import { analyticsTestimonials } from '../../common/Testimonials'
import { ProblemBox } from '../../components/ProblemBox'
import { analyticsProblems, analyticsProblemStatement } from '../../common/ProblemContent'

export default function Analytics() {
    const [demoOpen, setDemoOpen] = useState(false)
    const [videoOpen, setVideoOpen] = useState(false)

    return (
        <Box>
            <PageMeta
                title="CFP Analytics"
                description="Strategic market intelligence for UK & Ireland public sector. 8 specialist dashboards covering buyers, suppliers, regions, industries and contract pipelines. Build a data-driven BD strategy."
                canonical="/products/analytics"
            />
            {/* Hero */}
            <Box sx={{
                background: `linear-gradient(135deg, ${FORESIGHT_HERO_START} 0%, ${FORESIGHT_HERO_MID} 50%, ${FORESIGHT_HERO_END} 100%)`,
                ...SECTION_DARK,
                pt: { xs: 4, md: 6 },
            }}>
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                display: 'inline-flex', alignItems: 'center', gap: 1.5,
                                backgroundColor: `${COLOR_QUATERNARY}12`,
                                border: `1px solid ${COLOR_QUATERNARY}70`,
                                borderRadius: 2, px: 2, py: 1, mb: 3
                            }}>
                                <Box sx={{
                                    width: 32, height: 32, borderRadius: 1,
                                    backgroundColor: `${COLOR_QUATERNARY}20`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <InsightsIcon sx={{ color: COLOR_QUATERNARY, fontSize: 22 }} />
                                </Box>
                                <Typography variant="caption" sx={{ color: COLOR_QUATERNARY, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>
                                    Market Intelligence
                                </Typography>
                            </Box>

                            <Typography variant="h1" sx={{ color: 'white', fontSize: { xs: '36px', md: '52px' }, mb: 2 }}>
                                CFP <Box component="span" sx={{ color: COLOR_QUATERNARY }}>Analytics</Box>
                            </Typography>
                            <Typography variant="h4" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 400, mb: 3, lineHeight: 1.5 }}>
                                Deep Analytics Dashboards for UK & Ireland Public Sector Procurement
                            </Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '17px', mb: 4, lineHeight: 1.7 }}>
                                CFP Analytics is a suite of 8 specialist data analytics dashboards. Do a deep dive on the opportunities available for your business, understand how buyers go to market, research your competitors and get the strategic intelligence you need to win more public sector contracts.
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <FancyGoldButton color={COLOR_QUATERNARY} endIcon={<ArrowForwardIcon />} onClick={() => setDemoOpen(true)}>
                                    Book a Demo
                                </FancyGoldButton>
                                <FancyLightButton to="/contactus" color={COLOR_QUATERNARY}>
                                    Talk to the Team
                                </FancyLightButton>
                            </Stack>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                background: 'rgba(255,255,255,0.04)',
                                border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: 3,
                                p: 3,
                            }}>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600 }}>
                                    Why Analytics?
                                </Typography>
                                <Stack spacing={1.5} sx={{ mt: 2 }}>
                                    {analyticsHighlights.map(h => (
                                        <Box key={h} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                            <CheckCircleIcon sx={{ color: COLOR_QUATERNARY, fontSize: CHECKCIRCLE_SIZE, mt: 0.15, flexShrink: 0 }} />
                                            <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '14px' }}>{h}</Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />
            {/* Demo video */}
            <Box sx={{ ...SECTION_LIGHT }}>
                <Container maxWidth="md">
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Chip
                            icon={<PlayCircleIcon sx={{ fontSize: '16px !important', color: `${COLOR_QUATERNARY} !important` }} />}
                            label="Quick Tour"
                            size="medium"
                            sx={{
                                backgroundColor: `${COLOR_QUATERNARY}15`,
                                color: COLOR_QUATERNARY,
                                border: `1px solid ${COLOR_QUATERNARY}35`,
                                fontWeight: 600,
                                fontSize: '12px',
                                mb: 2,
                            }}
                        />
                        <Typography variant="h3" sx={{ mb: 1 }}>See CFP Analytics in action</Typography>
                        <Typography color="text.secondary">
                            A walkthrough of all 8 dashboards search, opportunities, buyers, suppliers, regions, industry, KPIs and social indicators.
                        </Typography>
                    </Box>

                    {/* Thumbnail */}
                    <Box
                        onClick={() => setVideoOpen(true)}
                        sx={{
                            position: 'relative',
                            borderRadius: 3,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            boxShadow: '0 8px 40px rgba(0,0,0,0.15)',
                            '&:hover .play-overlay': { backgroundColor: 'rgba(0,0,0,0.45)' },
                            '&:hover .play-icon': { transform: 'scale(1.12)' },
                        }}
                    >
                        <Box
                            component="img"
                            src={`https://img.youtube.com/vi/${ANALYTICS_VIDEO_ID}/maxresdefault.jpg`}
                            alt="CFP Analytics product demo"
                            sx={{ width: '100%', display: 'block' }}
                        />
                        <Box className="play-overlay" sx={{
                            position: 'absolute', inset: 0,
                            backgroundColor: 'rgba(0,0,0,0.3)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'background-color 0.2s',
                        }}>
                            <PlayCircleIcon className="play-icon" sx={{
                                fontSize: 72,
                                color: 'white',
                                filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.5))',
                                transition: 'transform 0.2s',
                            }} />
                        </Box>
                    </Box>
                </Container>
            </Box>
            {/* Problem bar */}
            <WaveDivider top={BG_LIGHT} bottom={DARK_800} />
            {/* Problem bar */}
            <ProblemBox quote={analyticsProblemStatement} items={analyticsProblems} color={COLOR_QUATERNARY} />
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />

            <HowItWorksSection
                title={analyticsHIWTitle}
                subtitle={analyticsHIWSubtitle}
                steps={analyticsSteps}
            />

            {/* Stats strip */}
            <StatsSection stats={analyticsStats} />

            {/* Features grid */}
            <Box sx={{ ...SECTION_LIGHT }}>
                <Container maxWidth="lg">
                    <Typography variant="h2" sx={{ textAlign: 'center', mb: 2 }}>8 Dashboards. Complete Market Intelligence.</Typography>
                    <Typography color="text.secondary" sx={{ textAlign: 'center', mb: 8, maxWidth: 560, mx: 'auto' }}>
                        Every dashboard is designed to give you specific, actionable insights across the UK & Ireland public sector procurement market.
                    </Typography>
                    <Grid container spacing={4}>
                        {analyticsFeatures.map(feature => (
                            <Grid item xs={12} sm={6} md={3} key={feature.title}>
                                <Card sx={{ height: '100%' }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Box sx={{
                                            width: 44, height: 44, borderRadius: 2,
                                            backgroundColor: `${COLOR_QUATERNARY}15`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            mb: 2,
                                        }}>
                                            <feature.Icon sx={{ color: COLOR_QUATERNARY, fontSize: 36 }} />
                                        </Box>
                                        <Typography variant="h6" sx={{ mb: 1, fontSize: '15px' }}>{feature.title}</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, fontSize: '13px' }}>{feature.body}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            <WaveDivider top={BG_LIGHT} bottom={DARK_800} />
            {/* Testimonials */}
            <Box sx={{ ...SECTION_DARK }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Box
                            component="a"
                            href={REVIEWS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
                                gap: 0.75, textDecoration: 'none', cursor: 'pointer',
                                borderRadius: 8, px: 5, py: 1,
                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
                                '&:hover .review-label': { textDecoration: 'underline' },
                            }}
                        >
                            <Box className="star-row" sx={{ display: 'flex', gap: 0.5, transition: 'opacity 0.2s' }}>
                                {[...Array(5)].map((_, i) => (
                                    <StarIcon key={i} sx={{ color: COLOR_QUATERNARY, fontSize: 20 }} />
                                ))}
                            </Box>
                            <Typography className="review-label" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', transition: 'color 0.2s' }}>
                                5.0 on Reviews.io · 52 reviews · 100% recommend
                                <OpenInNewIcon sx={{ fontSize: 14, verticalAlign: 'text-bottom', ml: 0.5 }} />
                            </Typography>
                        </Box>
                    </Box>
                    <Grid container spacing={4}>
                        {analyticsTestimonials.map(t => (
                            <Grid item xs={12} md={4} key={t.name}>
                                <Box sx={{
                                    backgroundColor: 'rgba(255,255,255,0.04)',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                    borderRadius: 3,
                                    p: 3.5,
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}>
                                    <FormatQuoteIcon sx={{ color: COLOR_QUATERNARY, fontSize: 32, mb: 2, opacity: 0.7 }} />
                                    <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '15px', lineHeight: 1.7, flex: 1, mb: 3, fontStyle: 'italic' }}>
                                        "{t.quote}"
                                    </Typography>
                                    <Box>
                                        <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '14px' }}>{t.name}</Typography>
                                        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}>✓ Verified Buyer · {t.timeAgo}</Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />
            {/* Who uses */}
            <Box sx={{ ...SECTION_LIGHT }}>
                <Container maxWidth="md">
                    <Box sx={{
                        background: `linear-gradient(135deg, ${COLOR_QUATERNARY}10, ${COLOR_QUATERNARY}05)`,
                        border: `1px solid ${COLOR_QUATERNARY}25`,
                        borderRadius: 3,
                        p: { xs: 4, md: 6 },
                    }}>
                        <Typography variant="h4" sx={{ mb: 3 }}>Who uses CFP Analytics?</Typography>
                        <Grid container spacing={3}>
                            {analyticsWhoUses.map(item => (
                                <Grid item xs={12} sm={6} key={item.role}>
                                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                                        <CheckCircleIcon sx={{ color: COLOR_QUATERNARY, fontSize: CHECKCIRCLE_SIZE, mt: 0.2, flexShrink: 0 }} />
                                        <Box>
                                            <Typography variant="body2" fontWeight={700}>{item.role}</Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{item.desc}</Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Container>
            </Box>

            <WaveDivider top={BG_LIGHT} bottom={DARK_800} />
            {/* FAQ */}
            <Box sx={{ ...SECTION_DARK }}>
                <Container maxWidth="md">
                    <Typography variant="h2" sx={{ textAlign: 'center', mb: 2, color: 'white' }}>Common questions</Typography>
                    <Typography sx={{ textAlign: 'center', mb: 6, color: 'rgba(255,255,255,0.55)' }}>
                        Everything you need to know before you start.
                    </Typography>
                    <Stack spacing={1.5}>
                        {analyticsFaqs.map(faq => (
                            <Accordion key={faq.q} disableGutters elevation={0} sx={{
                                border: '1px solid rgba(0,0,0,0.08)',
                                borderRadius: '10px !important',
                                '&:before': { display: 'none' },
                                '&.Mui-expanded': { border: `1px solid ${COLOR_QUATERNARY}40` },
                            }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3, py: 0.5 }}>
                                    <Typography sx={{ fontWeight: 600, fontSize: '15px' }}>{faq.q}</Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: 3, pb: 2.5 }}>
                                    <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>{faq.a}</Typography>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Stack>
                </Container>
            </Box>
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />
            <TrustedPartnerSection />

            <CtaSection
                title="Ready to see the market differently?"
                subtitle="Start your 14-day free trial today. Full access, no commitment."
                onDemoClick={() => setDemoOpen(true)}
            />

            <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} videoId={ANALYTICS_VIDEO_ID} title="CFP Analytics demo" />
            <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
        </Box>
    )
}
